'use client';

import { useEffect, useMemo, useState } from 'react';
import { ensureFontReady } from '../lib/tapestryText';
import { createTapestryReplay } from '../lib/tapestryReplay';

export default function useTapestryReplay(timeline, time, options, detail) {
  const { threshold, catalog, seed, canvasHeight, keywords, tags, fontFamily, forcedBackgroundId } = options;
  const [fontReady, setFontReady] = useState(null);
  useEffect(() => {
    if (!timeline) return;
    let cancelled = false;
    ensureFontReady(fontFamily).then(() => !cancelled && setFontReady(fontFamily));
    return () => {
      cancelled = true;
    };
  }, [timeline, fontFamily]);
  const replay = useMemo(
    () =>
      timeline && canvasHeight > 0 && keywords && tags && fontReady === fontFamily
        ? createTapestryReplay(timeline, {
            threshold,
            catalog,
            seed,
            canvasHeight,
            keywords,
            tags,
            fontFamily,
            forcedBackgroundId,
          })
        : null,
    [timeline, threshold, catalog, seed, canvasHeight, keywords, tags, fontFamily, forcedBackgroundId, fontReady]
  );
  const index = replay?.indexAt(time) ?? -1;
  const [result, setResult] = useState(null);
  const cached = replay?.peek(time, detail) || null;
  useEffect(() => {
    if (!replay || cached) return;
    let cancelled = false;
    replay
      .prepare(time, detail, () => cancelled)
      .then((ready) => {
        if (ready && !cancelled) setResult({ replay, index, detail, frame: replay.peek(time, detail) });
      })
      .catch((error) => {
        if (!cancelled) setResult({ replay, index, detail, error });
      });
    return () => {
      cancelled = true;
    };
    // The event index, not each audio tick, changes the scene being prepared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replay, index, detail, cached]);
  if (cached) return cached;
  if (result?.replay === replay && result.index === index && result.detail === detail) {
    return result.error ? { error: result.error, placements: [] } : result.frame;
  }
  return null;
}
